import { useState } from "react";
import { Loader2, ShieldCheck, Trash2, AlertTriangle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function Account() {
  const { user } = useAuth();

  return (
    <div className="mx-auto max-w-2xl animate-in fade-in-0 slide-in-from-bottom-2 duration-500" data-testid="account-page">
      <h1 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">Account &amp; Privacy</h1>
      <p className="mt-1 text-sm text-muted-foreground">Manage your sign-in details and your personal data.</p>

      <div className="mt-6 rounded-2xl border border-border/80 bg-card p-6 shadow-sm">
        <h2 className="font-heading font-semibold">Profile</h2>
        <dl className="mt-4 space-y-2 text-sm">
          <Row label="Name" value={user?.name} />
          <Row label="Email" value={user?.email} testId="account-email" />
          <Row label="Email verified" value={user?.email_verified ? "Yes" : "Not yet"} />
        </dl>
      </div>

      <ChangePasswordCard />
      <DeleteAccountCard isSuperadmin={user?.is_superadmin} />

      <p className="mt-6 text-xs leading-relaxed text-muted-foreground">
        Under GDPR you can request access to, correction of, or a copy of your personal data at any time — email{" "}
        <a href="mailto:support@hireqix.com" className="font-medium text-primary hover:underline">support@hireqix.com</a> and we'll
        respond within 30 days.
      </p>
    </div>
  );
}

function Row({ label, value, testId }) {
  return (
    <div className="flex items-center justify-between border-b border-border/50 pb-2 last:border-0">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium" data-testid={testId}>{value || "—"}</dd>
    </div>
  );
}

function ChangePasswordCard() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated — please sign in again");
      setTimeout(() => window.location.assign("/login"), 1200);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-6 rounded-2xl border border-border/80 bg-card p-6 shadow-sm">
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-4 w-4 text-primary" />
        <h2 className="font-heading font-semibold">Change password</h2>
      </div>
      <p className="mt-1.5 text-sm text-muted-foreground">Changing your password signs you out of every other device.</p>
      <form onSubmit={submit} className="mt-4 space-y-3">
        <Input type="password" required placeholder="Current password" value={current} onChange={(e) => setCurrent(e.target.value)} data-testid="current-password-input" />
        <Input type="password" required minLength={8} placeholder="New password" value={next} onChange={(e) => setNext(e.target.value)} data-testid="new-password-input" />
        <p className="text-xs text-muted-foreground">At least 8 characters, including a letter and a number.</p>
        <Button type="submit" disabled={busy} data-testid="change-password-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update Password"}
        </Button>
      </form>
    </div>
  );
}

function DeleteAccountCard({ isSuperadmin }) {
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/delete-account", { password });
      toast.success("Your account and data have been permanently deleted");
      localStorage.removeItem("activeWorkspaceId");
      setTimeout(() => window.location.assign("/"), 1200);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-6 rounded-2xl border border-destructive/40 bg-destructive/[0.03] p-6 shadow-sm" data-testid="delete-account-card">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-destructive" />
        <h2 className="font-heading font-semibold text-destructive">Delete my account &amp; data</h2>
      </div>
      <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
        This permanently erases your account, every workspace you own, all jobs, uploaded resume data and candidate evaluations.
        Unused credits are forfeited and this cannot be undone.
      </p>
      {isSuperadmin ? (
        <p className="mt-3 text-xs font-medium text-muted-foreground">Superadmin accounts cannot be self-deleted.</p>
      ) : !open ? (
        <Button variant="outline" className="mt-4 border-destructive/50 text-destructive hover:bg-destructive/10" onClick={() => setOpen(true)} data-testid="open-delete-account-btn">
          <Trash2 className="h-4 w-4" /> Delete my account
        </Button>
      ) : (
        <form onSubmit={submit} className="mt-4 space-y-3">
          <Input type="password" required placeholder="Confirm your password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="delete-account-password-input" />
          <Input required placeholder='Type DELETE to confirm' value={confirmText} onChange={(e) => setConfirmText(e.target.value)} data-testid="delete-account-confirm-input" />
          <div className="flex items-center gap-2">
            <Button
              type="submit"
              disabled={busy || confirmText !== "DELETE"}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-account-btn"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Permanently delete"}
            </Button>
            <Button type="button" variant="ghost" onClick={() => setOpen(false)} data-testid="cancel-delete-account-btn">Cancel</Button>
          </div>
        </form>
      )}
    </div>
  );
}
